
import React, { useState } from 'react';
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval, isSameMonth, isToday, isSameDay } from 'date-fns';
import { useCalendarCampaigns, useCalendarEvents } from '@/hooks/useCalendarData';
import CalendarCampaignPill from './CalendarCampaignPill';
import CalendarEventDot from './CalendarEventDot';
import CalendarDayModal from './CalendarDayModal';

interface CalendarGridProps {
  currentDate: Date;
  view: 'month' | 'week' | 'day';
  showCampaigns: boolean;
  showEvents: boolean;
}

const isCampaignActiveOnDay = (campaign: { start_date: string | null; end_date: string | null }, day: Date) => {
  if (!campaign.start_date || !campaign.end_date) return false;
  const dayStr = format(day, 'yyyy-MM-dd');
  const startStr = campaign.start_date.substring(0, 10);
  const endStr = campaign.end_date.substring(0, 10);
  return dayStr >= startStr && dayStr <= endStr;
};

const isEventOnDay = (event: { event_date: string }, day: Date) => {
  if (!event.event_date) return false;
  const dayStr = format(day, 'yyyy-MM-dd');
  const eventDateStr = event.event_date.substring(0, 10);
  return dayStr === eventDateStr;
};

const CalendarGrid: React.FC<CalendarGridProps> = ({
  currentDate,
  view,
  showCampaigns,
  showEvents,
}) => {
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);

  const getDateRange = () => {
    if (view === 'day') {
      return { start: currentDate, end: currentDate };
    } else if (view === 'week') {
      return {
        start: startOfWeek(currentDate, { weekStartsOn: 1 }),
        end: endOfWeek(currentDate, { weekStartsOn: 1 }),
      };
    } else {
      const monthStart = startOfMonth(currentDate);
      return {
        start: startOfWeek(monthStart, { weekStartsOn: 1 }),
        end: endOfWeek(endOfMonth(currentDate), { weekStartsOn: 1 }),
      };
    }
  };

  const { start: startDate, end: endDate } = getDateRange();
  const { data: campaigns = [], isLoading: loadingCampaigns } = useCalendarCampaigns(startDate, endDate);
  const { data: events = [], isLoading: loadingEvents } = useCalendarEvents(startDate, endDate);

  const days = eachDayOfInterval({ start: startDate, end: endDate });

  if (loadingCampaigns || loadingEvents) {
    return (
      <div className="flex-1 flex items-center justify-center bg-white rounded-xl shadow-sm border">
        <div className="text-muted-foreground">Loading calendar...</div>
      </div>
    );
  }

  const renderMonthView = () => {
    const weeks: Date[][] = [];
    for (let i = 0; i < days.length; i += 7) {
      weeks.push(days.slice(i, i + 7));
    }

    return (
      <div className="flex-1 bg-white rounded-xl shadow-sm border overflow-hidden">
        {/* Header */}
        <div className="grid grid-cols-7 border-b bg-gray-50/50">
          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => (
            <div key={day} className="p-4 text-sm font-medium text-muted-foreground text-center">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7">
          {days.map((day, index) => {
            const dayEvents = events.filter(event => isEventOnDay(event, day));
            const dayCampaigns = campaigns.filter(campaign => isCampaignActiveOnDay(campaign, day));

            const visibleCampaigns = showCampaigns ? dayCampaigns.slice(0, 2) : [];
            const visibleEvents = showEvents ? dayEvents.slice(0, 2) : [];
            const remainingCount = (showCampaigns ? dayCampaigns.length : 0) + (showEvents ? dayEvents.length : 0) - visibleCampaigns.length - visibleEvents.length;

            return (
              <div
                key={day.toISOString()}
                className={`min-h-[120px] border-r border-b p-3 cursor-pointer hover:bg-gray-50/50 transition-colors ${
                  !isSameMonth(day, currentDate) ? 'bg-gray-50/30' : ''
                } ${index % 7 === 6 ? 'border-r-0' : ''}`}
                onClick={() => (dayCampaigns.length > 0 || dayEvents.length > 0) && setSelectedDay(day)}
              >
                {/* <div className={`text-sm font-medium mb-2 ${
                  isToday(day) ? 'bg-primary text-primary-foreground rounded-full w-7 h-7 flex items-center justify-center' : 
                  !isSameMonth(day, currentDate) ? 'text-muted-foreground' : 'text-foreground'
                }`}> */}
                <div className={`text-sm font-medium mb-2 ${
                  isToday(day) ? 'bg-purple-600 text-white rounded-full w-7 h-7 flex items-center justify-center' : 
                  !isSameMonth(day, currentDate) ? 'text-muted-foreground' : 'text-foreground'
                }`}>
                  {format(day, 'd')}
                </div>

                <div className="space-y-1">
                  {visibleCampaigns.map((campaign) => (
                    <CalendarCampaignPill key={campaign.id} campaign={campaign} compact />
                  ))}
                  
                  {visibleEvents.map((event) => (
                    <CalendarEventDot key={event.id} event={event} compact />
                  ))}

                  {remainingCount > 0 && (
                    <div className="text-xs text-muted-foreground font-medium hover:text-foreground transition-colors cursor-pointer">
                      +{remainingCount} more
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderWeekView = () => {
    const weekDays = days.slice(0, 7);
    
    return (
      <div className="flex-1 bg-white rounded-xl shadow-sm border overflow-hidden">
        {/* Header */}
        <div className="grid grid-cols-7 border-b bg-gray-50/50">
          {weekDays.map((day) => (
            <div key={day.toISOString()} className="p-4 text-center border-r last:border-r-0">
              <div className="text-xs text-muted-foreground">{format(day, 'EEE')}</div>
              <div className={`text-lg font-semibold mt-1 ${
                isToday(day) ? 'bg-purple-600 text-white rounded-full w-8 h-8 flex items-center justify-center mx-auto' : 'text-foreground'
              }`}>
                {format(day, 'd')}
              </div>
            </div>
          ))}
        </div>

        {/* Week Grid */}
        <div className="grid grid-cols-7">
          {weekDays.map((day, index) => {
            const dayEvents = events.filter(event => isEventOnDay(event, day));
            const dayCampaigns = campaigns.filter(campaign => isCampaignActiveOnDay(campaign, day));

            return (
              <div 
                key={day.toISOString()} 
                className={`min-h-[400px] border-r last:border-r-0 p-3 cursor-pointer hover:bg-gray-50/50 transition-colors`}
                onClick={() => (dayCampaigns.length > 0 || dayEvents.length > 0) && setSelectedDay(day)}
              >
                <div className="space-y-2">
                  {showCampaigns && dayCampaigns.map((campaign) => (
                    <CalendarCampaignPill key={campaign.id} campaign={campaign} />
                  ))}
                  {showEvents && dayEvents.map((event) => (
                    <CalendarEventDot key={event.id} event={event} showTime />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderDayView = () => {
    const dayEvents = events.filter(event => isEventOnDay(event, currentDate));
    const dayCampaigns = campaigns.filter(campaign => isCampaignActiveOnDay(campaign, currentDate));

    return (
      <div className="flex-1 bg-white rounded-xl shadow-sm border overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b bg-gray-50/50">
          <div className="text-xl font-semibold text-foreground">
            {format(currentDate, 'EEEE, MMMM d, yyyy')}
          </div>
        </div>

        {/* Day Content */}
        <div className="p-6">
          <div className="space-y-6">
            {showCampaigns && dayCampaigns.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-muted-foreground mb-3 uppercase tracking-wide">Active Campaigns</h3>
                <div className="space-y-2">
                  {dayCampaigns.map((campaign) => (
                    <CalendarCampaignPill key={campaign.id} campaign={campaign} expanded />
                  ))}
                </div>
              </div>
            )}
            
            {showEvents && dayEvents.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-muted-foreground mb-3 uppercase tracking-wide">Events</h3>
                <div className="space-y-2">
                  {dayEvents.map((event) => (
                    <CalendarEventDot key={event.id} event={event} expanded showTime />
                  ))}
                </div>
              </div>
            )}

            {(!showCampaigns || dayCampaigns.length === 0) && (!showEvents || dayEvents.length === 0) && (
              <div className="text-center text-muted-foreground py-12">
                <div className="text-lg font-medium mb-2">No items scheduled</div>
                <div className="text-sm">Enjoy your free day!</div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {view === 'day' && renderDayView()}
      {view === 'week' && renderWeekView()}
      {view === 'month' && renderMonthView()}
      
      {selectedDay && (
        <CalendarDayModal
          date={selectedDay}
          campaigns={campaigns}
          events={events}
          showCampaigns={showCampaigns}
          showEvents={showEvents}
          onClose={() => setSelectedDay(null)}
        />
      )}
    </>
  );
};

export default CalendarGrid;
